// Centralized schema.org JSON-LD builders for Cedar Creek Construction.
// Keep all structured-data shape decisions here so pages stay DRY and
// schemas validate consistently at https://validator.schema.org/.

import { SITE_ORIGIN } from "./seo";

const SITE_URL = SITE_ORIGIN;
// Served from public/ so the URL is stable. Never point this at a hashed
// build asset — the hash changes every build and the schema logo 404s.
const LOGO_URL = `${SITE_URL}/logo.png`;

export const ROOFING_CONTRACTOR_ID = `${SITE_URL}/#business`;

export const buildRoofingContractorSchema = () => ({
  "@context": "https://schema.org",
  "@type": "RoofingContractor",
  "@id": ROOFING_CONTRACTOR_ID,
  name: "Cedar Creek Construction",
  description:
    "Expert roofing, siding, deck, and gutter services. Licensed contractor serving Minneapolis and surrounding Minnesota communities.",
  url: SITE_URL,
  logo: LOGO_URL,
  image: LOGO_URL,
  telephone: "+1-612-298-9865",
  email: "chuck@cedarcreekconstruct.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "23383 Redwood Ct NW",
    addressLocality: "St Francis",
    addressRegion: "MN",
    postalCode: "55070",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 45.3866,
    longitude: -93.3597,
  },
  areaServed: [
    "Minneapolis",
    "Andover",
    "Coon Rapids",
    "East Bethel",
    "Blaine",
    "Anoka",
    "Saint Francis",
    "Cambridge",
    "Isanti",
    "Cedar",
    "Bethel",
  ].map((city) => ({
    "@type": "City",
    name: city,
    containedInPlace: { "@type": "State", name: "Minnesota" },
  })),
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "06:00",
      closes: "21:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "09:00",
      closes: "21:00",
    },
  ],
  sameAs: ["https://www.facebook.com/cedarcreekconstructioninc"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Exterior Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Roof Replacement" },
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Roof Repair" },
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Siding Replacement" },
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Siding Repair" },
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Deck Installation" },
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Gutter Installation" },
      },
    ],
  },
  // Note: schema.org has no canonical "licenseNumber" property on
  // RoofingContractor; using `identifier` keeps it valid.
  identifier: "MN License #BC645307",
});

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "Cedar Creek Construction",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/favicon.png`,
    width: 512,
    height: 512,
  },
  image: `${SITE_URL}/favicon.png`,
  telephone: "+1-612-298-9865",
  email: "chuck@cedarcreekconstruct.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "23383 Redwood Ct NW",
    addressLocality: "St Francis",
    addressRegion: "MN",
    postalCode: "55070",
    addressCountry: "US",
  },
  sameAs: ["https://www.facebook.com/cedarcreekconstructioninc"],
});

export const buildWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_URL,
  name: "Cedar Creek Construction",
  publisher: { "@id": ORGANIZATION_ID },
  inLanguage: "en-US",
});

export interface FaqItem {
  question: string;
  answer: string;
}

export const buildFaqPageSchema = (faqs: FaqItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs
    .filter((f) => f.question?.trim() && f.answer?.trim())
    .map((faq) => ({
      "@type": "Question",
      name: faq.question.trim(),
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer.trim(),
      },
    })),
});

export const buildServiceSchema = ({
  name,
  description,
  url,
  serviceType,
}: {
  name: string;
  description: string;
  url: string;
  serviceType: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  serviceType,
  url,
  areaServed: {
    "@type": "AdministrativeArea",
    name: "Minneapolis–Saint Paul Metro, Minnesota",
  },
  provider: {
    "@type": "RoofingContractor",
    "@id": ROOFING_CONTRACTOR_ID,
    name: "Cedar Creek Construction",
    telephone: "+1-612-298-9865",
    url: SITE_URL,
  },
});
